/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
  Grid,
} from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { CustomIconButton, CustomTooltip } from '@opengeoweb/shared';
import { Add, Delete, Edit, Visibility, Cached } from '@opengeoweb/theme';
import { useTimeseriesTranslation } from '../../utils/i18n';
import type { Parameter } from '../TimeSeries/types';

export const sortByService = (
  serviceObj: TimeSeriesService[],
): TimeSeriesService[] => {
  // Need to make a copy as we are not allowed to mutate the original serviceObj and .sort mutates
  const newServiceObj = serviceObj.slice();
  return newServiceObj.sort((serviceA, serviceB) =>
    (serviceA.name ? serviceA.name : serviceA.id).localeCompare(
      serviceB.name ? serviceB.name : serviceB.id,
    ),
  );
};

interface ServiceOptionsDialogProps {
  services: TimeSeriesService[] | undefined;
  parametersInUse: Parameter[];
  openNewServicePopup: () => void;
  openServicePopup: (variant: 'edit' | 'show', id: string, url: string) => void;
  removeService: (id: string, message?: string) => void;
}

const styles = {
  servicesContainer: {
    width: '360px',
    maxHeight: '412px',
    top: '140px',
    backgroundColor: 'geowebColors.background.surface',
    boxShadow: 6,
    overflow: 'auto',
  },
  header: {
    position: 'sticky',
    top: 0,
    fontSize: 'default',
    padding: '12px',
    backgroundColor: 'geowebColors.background.surface',
    zIndex: 100,
  },
  loading: {
    position: 'absolute',
    zIndex: 101,
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'geowebColors.background.surface',
    zIndex: 100,
  },
  button: {
    margin: '16px',
    width: '80%',
    fontSize: '12px',
    textTransform: 'none',
  },
};

const stopTabPropagation = (event: React.KeyboardEvent): void => {
  if (event.key === 'Tab') {
    event.stopPropagation();
  }
};

export const ServiceOptionsDialog: React.FC<ServiceOptionsDialogProps> = ({
  services,
  parametersInUse,
  openNewServicePopup,
  openServicePopup,
  removeService,
}: ServiceOptionsDialogProps) => {
  const dialogRef = React.useRef<HTMLElement | null>(null);
  const { t } = useTimeseriesTranslation();

  React.useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  if (!services || services.length === 0) {
    return (
      <Box
        onKeyDown={(event): void => {
          if (event.key !== 'Escape') {
            event.stopPropagation();
          }
        }}
        ref={dialogRef}
        sx={{
          backgroundColor: 'geowebColors.background.surface',
          boxShadow: 6,
          padding: 1,
        }}
        tabIndex={-1}
      >
        {t('timeseries-no-services')}
      </Box>
    );
  }

  return (
    <Box
      aria-label={t('timeseries-services-options')}
      onKeyDown={(event): void => {
        if (event.key !== 'Escape') {
          event.stopPropagation();
        }
      }}
      ref={dialogRef}
      sx={styles.servicesContainer}
      tabIndex={-1}
    >
      <Box sx={styles.header}>{t('timeseries-services')}</Box>
      <Rows
        openServicePopup={openServicePopup}
        parametersInUse={parametersInUse}
        removeService={removeService}
        services={services}
      />
      <Box sx={styles.footer}>
        <Button
          data-testid="add-new-ts-service"
          onClick={openNewServicePopup}
          onKeyDown={stopTabPropagation}
          startIcon={<Add />}
          sx={styles.button}
          variant="tertiary"
        >
          {t('timeseries-add-service')}
        </Button>
      </Box>
    </Box>
  );
};

const deleteServiceTitle = (hasActiveLayers: boolean, t: TFunction): string => {
  return hasActiveLayers
    ? t('timeseries-cannot-remove')
    : t('timeseries-delete-service');
};

const tooltipPopperProps = {
  disablePortal: true,
  sx: {
    '& .MuiTooltip-tooltip': {
      width: '200px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
};

const hasServiceParmetersInUse = (
  serviceId: string,
  parametersInUse: Parameter[],
): boolean => {
  return parametersInUse.some((parameter) => parameter.serviceId === serviceId);
};

const Rows: React.FC<{
  services: TimeSeriesService[];
  parametersInUse: Parameter[];
  openServicePopup: (variant: 'edit' | 'show', id: string, url: string) => void;
  removeService: (id: string, message?: string) => void;
}> = ({ services, parametersInUse, openServicePopup, removeService }) => {
  const sortedServices = sortByService(services);
  const { t } = useTimeseriesTranslation();

  return (
    <List disablePadding>
      {sortedServices.map((service) => {
        const { url, name, id, scope } = service;
        const serviceHasParametersInUse = hasServiceParmetersInUse(
          id,
          parametersInUse,
        );
        const userAddedService = scope === 'user';

        const openDialog = (): void =>
          openServicePopup(userAddedService ? 'edit' : 'show', id, url);

        return (
          <ListItem
            key={url}
            secondaryAction={
              <Grid
                container
                sx={{
                  justifyContent: 'left',
                  marginRight: '0px',
                  width: 96,
                  button: { marginLeft: 1 },
                }}
              >
                <CustomTooltip
                  placement="left"
                  slotProps={{ popper: tooltipPopperProps }}
                  title={t('timeseries-update-service')}
                >
                  <span>
                    <CustomIconButton
                      aria-label={t('timeseries-update-service')}
                      disabled
                      onClick={(): void => {}}
                      onKeyDown={stopTabPropagation}
                    >
                      <Cached />
                    </CustomIconButton>
                  </span>
                </CustomTooltip>
                {userAddedService ? (
                  <CustomTooltip
                    placement="left"
                    slotProps={{ popper: tooltipPopperProps }}
                    title={t('timeseries-edit-service')}
                  >
                    <span>
                      <CustomIconButton
                        aria-label={t('timeseries-edit-service')}
                        data-testid={`edit-button-${id}`}
                        onClick={openDialog}
                        onKeyDown={stopTabPropagation}
                      >
                        <Edit />
                      </CustomIconButton>
                    </span>
                  </CustomTooltip>
                ) : (
                  <CustomTooltip
                    placement="left"
                    slotProps={{ popper: tooltipPopperProps }}
                    title={t('timeseries-show-service')}
                  >
                    <span>
                      <CustomIconButton
                        aria-label={t('timeseries-show-service')}
                        data-testid={`show-button-${id}`}
                        onClick={openDialog}
                        onKeyDown={stopTabPropagation}
                      >
                        <Visibility />
                      </CustomIconButton>
                    </span>
                  </CustomTooltip>
                )}
                {!!userAddedService && (
                  <Box>
                    <CustomTooltip
                      placement="left"
                      slotProps={{ popper: tooltipPopperProps }}
                      title={deleteServiceTitle(serviceHasParametersInUse, t)}
                    >
                      <span>
                        <CustomIconButton
                          aria-label={t('timeseries-delete-service')}
                          data-testid={`remove-button-${id}`}
                          disabled={serviceHasParametersInUse}
                          onClick={() =>
                            removeService(
                              service.id,
                              t('timeseries-service-removed', {
                                service: service.name
                                  ? service.name
                                  : service.id,
                              }),
                            )
                          }
                          onKeyDown={stopTabPropagation}
                        >
                          <Delete />
                        </CustomIconButton>
                      </span>
                    </CustomTooltip>
                  </Box>
                )}
              </Grid>
            }
          >
            <ListItemText
              style={{
                fontSize: '16px',
              }}
            >
              <Typography
                noWrap
                style={{
                  textOverflow: 'ellipsis',
                  paddingRight: '70px',
                }}
              >
                {name || id}
              </Typography>
            </ListItemText>
          </ListItem>
        );
      })}
    </List>
  );
};
